import { TFunction } from 'i18next';
import { srpFilterInitialValues } from 'redux-implementations/srpFilters';
import {
  BedType,
  CabinType,
  CheckboxValue,
  ExteriorColorType,
  FilterPill,
  InteriorColorType,
  RearWheelType,
  SliderBlockValue,
  SrpBedTypeFilterValue,
  SrpCabinTypeFilterValue,
  SrpCategoryValue,
  SrpColorsFilterValue,
  SrpExteriorColorFilterValue,
  SrpFilterName,
  SrpInteriorColorFilterValue,
  SrpRearWheelFilterValue,
  SrpTruckOptionsFilterValue,
} from 'types/srpFilters';
import { stateNames } from 'components/Srp/SearchFilter/CategoryFilter/categoryNames';

export function createCommonPill(name: string, value: string): FilterPill[] {
  return [
    {
      name,
      value,
      label: value,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      resetState: { [name]: srpFilterInitialValues[name as SrpFilterName] },
    },
  ];
}

export function createBedLengthPill(t: TFunction, filter: SliderBlockValue): FilterPill[] | [] {
  const { min, max } = filter;
  const message = t('srp.pills.bedRange', { min, max });
  return [
    {
      name: 'BedLength',
      value: message,
      label: message,
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      resetState: { TruckOptions: { bedLength: srpFilterInitialValues.TruckOptions.bedLength } },
    },
  ];
}

export function createMakeModelPills(makeModelFilters: { [key: string]: string }): FilterPill[] {
  return Object.keys(makeModelFilters)
    .filter((f) => makeModelFilters[f])
    .map((makeModelFilter) => ({
      name: makeModelFilter,
      value: makeModelFilters[makeModelFilter],
      label: makeModelFilters[makeModelFilter],
      resetState: {
        MakeModel: {
          [makeModelFilter]: undefined,
          ...(makeModelFilter === 'make' && { model: undefined }),
        },
        Trims: [],
      },
    }));
}

export function createTrimsPills(trimFilters: string[]): FilterPill[] {
  return trimFilters.map((trim) => ({
    name: trim,
    value: trim,
    label: trim,
    resetState: { Trims: trimFilters.filter((t: string) => t !== trim) },
  }));
}

export function createColorPills(
  t: TFunction,
  stateKey: 'exterior' | 'interior',
  filterObj: SrpColorsFilterValue,
  suffix: string
): FilterPill[] {
  const checkboxStateMap: { [key: string]: string } = {
    white: t('srp.pills.white', { suffix }),
    gray: t('srp.pills.gray', { suffix }),
    black: t('srp.pills.black', { suffix }),
    yellow: t('srp.pills.yellow', { suffix }),
    orange: t('srp.pills.orange', { suffix }),
    brown: t('srp.pills.brown', { suffix }),
    red: t('srp.pills.red', { suffix }),
    green: t('srp.pills.green', { suffix }),
    blue: t('srp.pills.blue', { suffix }),
    purple: t('srp.pills.purple', { suffix }),
    silver: t('srp.pills.silver', { suffix }),
    gold: t('srp.pills.gold', { suffix }),
  };
  return Object.keys(filterObj[stateKey])
    .filter((f) => {
      if (stateKey === 'exterior') {
        const colors: SrpExteriorColorFilterValue = filterObj[stateKey];
        const colorKey = f as ExteriorColorType;
        return colors[colorKey];
      }
      const colors: SrpInteriorColorFilterValue = filterObj[stateKey];
      const colorKey = f as InteriorColorType;
      return colors[colorKey];
    })
    .map((checkboxFilter) => ({
      name: checkboxFilter,
      value: checkboxStateMap[checkboxFilter],
      label: checkboxStateMap[checkboxFilter],
      resetState: { Colors: { [stateKey]: { [checkboxFilter]: false } } },
    }));
}

export function createTruckOptionsPillsFunc(
  t: TFunction,
  stateKey: 'cabinType' | 'rearWheel' | 'bedType',
  filterObj: SrpTruckOptionsFilterValue,
  suffix?: string
): FilterPill[] {
  const checkboxStateMap: { [key: string]: string } = {
    extended: t('srp.pills.extended'),
    crew: t('srp.pills.crew'),
    single: t('srp.pills.single'),
    dual: t('srp.pills.dual'),
    short: t('srp.pills.short'),
    regular: t('srp.pills.regular', { suffix }),
    chassis: t('srp.pills.chassis'),
    stepSide: t('srp.pills.stepSide'),
    long: t('srp.pills.long'),
  };
  return (
    Object.keys(filterObj[stateKey])
      // eslint-disable-next-line array-callback-return, consistent-return
      .filter((f) => {
        // eslint-disable-next-line default-case
        switch (stateKey) {
          case 'cabinType': {
            const options: SrpCabinTypeFilterValue = filterObj[stateKey];
            const optionKey = f as CabinType;
            return options[optionKey];
          }
          case 'rearWheel': {
            const options: SrpRearWheelFilterValue = filterObj[stateKey];
            const optionKey = f as RearWheelType;
            return options[optionKey];
          }
          case 'bedType': {
            const options: SrpBedTypeFilterValue = filterObj[stateKey];
            const optionKey = f as BedType;
            return options[optionKey];
          }
        }
      })
      .map((checkboxFilter) => ({
        name: checkboxFilter,
        value: checkboxStateMap[checkboxFilter],
        label: checkboxStateMap[checkboxFilter],
        resetState: { TruckOptions: { [stateKey]: { [checkboxFilter]: false } } },
      }))
  );
}

export function createCheckboxPills(
  t: TFunction,
  stateKey: string,
  filterObj: CheckboxValue,
  suffix?: string
): FilterPill[] {
  const checkboxStateMap: { [key: string]: string } = {
    new: t('srp.pills.new'),
    used: t('srp.pills.used'),
    'certified pre-owned': t('srp.pills.certifiedPreOwned'),
    hideOnlineListings: t('srp.pills.hideOnlineListings'),
    hideListingsNoImages: t('srp.pills.hideListingsNoImages'),
    2: '2 ',
    3: '3 ',
    4: '4 ',
    5: '5 ',
    6: '6 ',
    8: '8 ',
    10: '10 ',
    12: '12 ',
    bluetooth: t('srp.pills.bluetooth'),
    backupCamera: t('srp.pills.backupCamera'),
    entertainmentDVD: t('srp.pills.entertainmentDVD'),
    handicapAccessible: t('srp.pills.handicapAccessible'),
    iPodAUXInput: t('srp.pills.iPodAUXInput'),
    laneDepartureWarning: t('srp.pills.laneDepartureWarning'),
    leather: t('srp.pills.leather'),
    navigationSystem: t('srp.pills.navigationSystem'),
    roofRack: t('srp.pills.roofRack'),
    sunroofMoonroof: t('srp.pills.sunroofMoonroof'),
    '3rdRowSeats': t('srp.pills.3rdRowSeats'),
    towing: t('srp.pills.towing'),
    manual: t('srp.pills.manual'),
    automatic: t('srp.pills.automatic'),
    frontWheel: t('srp.pills.frontWheel'),
    rearWheel: t('srp.pills.rearWheel'),
    allWheel: t('srp.pills.allWheel'),
    fourWheel: t('srp.pills.fourWheel'),
    convertible: t('srp.pills.convertible'),
    coupe: t('srp.pills.coupe'),
    crossover: t('srp.pills.crossover'),
    hatchback: t('srp.pills.hatchback'),
    hybrid: t('srp.pills.hybrid'),
    minivan: t('srp.pills.minivan'),
    passenger_cargo_van: t('srp.pills.passenger_cargo_van'),
    sedan: t('srp.pills.sedan'),
    suv: t('srp.pills.suv'),
    truck: t('srp.pills.truck'),
    wagon: t('srp.pills.wagon'),
    electric: t('srp.pills.electric'),
    diesel: t('srp.pills.diesel'),
    gasoline: t('srp.pills.gasoline'),
    hydrogenFuelCell: t('srp.pills.hydrogenFuelCell'),
    naturalGas: t('srp.pills.naturalGas'),
    pluginHybrid: t('srp.pills.pluginHybrid'),
    warranty: t('srp.pills.warranty'),
  };
  return Object.keys(filterObj)
    .filter((f) => filterObj[f])
    .map((checkboxFilter) => ({
      name: checkboxFilter,
      value: `${checkboxStateMap[checkboxFilter]}${suffix ?? ''}`,
      label: `${checkboxStateMap[checkboxFilter]}${suffix ?? ''}`,
      resetState: { [stateKey]: { [checkboxFilter]: false } },
    }));
}

export function createTruckOptionsPills(
  t: TFunction,
  truckOptionsFilters: SrpTruckOptionsFilterValue
): FilterPill[] {
  return [
    ...createTruckOptionsPillsFunc(t, 'cabinType', truckOptionsFilters, 'Cabin'),
    ...createTruckOptionsPillsFunc(t, 'rearWheel', truckOptionsFilters),
    ...createTruckOptionsPillsFunc(t, 'bedType', truckOptionsFilters, 'Bed'),
    ...(truckOptionsFilters.bedLength.min !== 3 && truckOptionsFilters.bedLength.max !== 9
      ? createBedLengthPill(t, truckOptionsFilters.bedLength)
      : []),
  ];
}

export function createRadiusPill(t: TFunction, value: number): FilterPill[] {
  const message = t('srp.pills.milesAway', { value });
  return [
    {
      name: message,
      value: message,
      label: message,
      resetState: { Radius: 50 },
    },
  ];
}

export function createCategoryPills(t: TFunction, filterValue: SrpCategoryValue): FilterPill[] {
  const valueToTranslationKeys: { [key: string]: string } = {
    'Any Category': 'any',
    American: 'american',
    Classic: 'classic',
    Commuter: 'commuter',
    Electric: 'electric',
    Family: 'family',
    'Fuel Efficient': 'fuelEfficient',
    Hybrid: 'hybrid',
    Large: 'large',
    Muscle: 'muscle',
    'Off Road': 'offRoad',
    Small: 'small',
    Sport: 'sport',
    Supercar: 'supercar',
  };

  const uiName = stateNames[filterValue] || filterValue;
  const message = t(`srp.pills.categories.${valueToTranslationKeys[uiName]}`);

  return [
    {
      name: message,
      value: message,
      label: message,
      resetState: { Category: srpFilterInitialValues[SrpFilterName.Category] },
    },
  ];
}

export type FilterPillFn = (value?: any) => FilterPill[];

export function createFilterPillFunctions(t: TFunction): Record<string, FilterPillFn> {
  return {
    Page: () => [],
    Limit: () => [],
    MakeModel: (filter: { [key: string]: string }): FilterPill[] => createMakeModelPills(filter),
    Trims: (filter: string[]): FilterPill[] => createTrimsPills(filter),
    Year: ({ min, max }: SliderBlockValue): FilterPill[] =>
      createCommonPill(
        'Year',
        `${min ?? srpFilterInitialValues.Year.min}-${`${max ?? srpFilterInitialValues.Year.max}`}`
      ),
    Condition: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'Condition', filters),
    Cylinders: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'Cylinders', filters, t('srp.pills.cylinders')),
    Radius: (value: number) => createRadiusPill(t, value),
    Price: (filter: SliderBlockValue): FilterPill[] => {
      const min = filter.min ?? srpFilterInitialValues.Price.min;
      const max = filter.max ?? srpFilterInitialValues.Price.max;
      return createCommonPill(
        'Price',
        `$${Number(min).toLocaleString()}-$${`${max.toLocaleString()}${max === 100000 ? '+' : ''}`}`
      );
    },
    Doors: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'Doors', filters, t('srp.pills.doors')),
    Advanced: (filters: CheckboxValue): FilterPill[] => createCheckboxPills(t, 'Advanced', filters),
    BodyTypes: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'BodyTypes', filters),
    Colors: (colorFilters: SrpColorsFilterValue): FilterPill[] => [
      ...createColorPills(t, 'exterior', colorFilters, t('srp.pills.ext')),
      ...createColorPills(t, 'interior', colorFilters, t('srp.pills.int')),
    ],
    Drivetrain: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'Drivetrain', filters),
    Powertrain: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'Powertrain', filters),
    FuelEfficiency: (value: number): FilterPill[] =>
      createCommonPill('FuelEfficiency', t('srp.pills.minMpg', { value })),
    FeaturesTechnology: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'FeaturesTechnology', filters),
    Mileage: (filter: SliderBlockValue): FilterPill[] => {
      const min = filter.min ?? srpFilterInitialValues.Mileage.min;
      const max = filter.max ?? srpFilterInitialValues.Mileage.max;
      return createCommonPill(
        'Mileage',
        t(
          max === srpFilterInitialValues.Mileage.max ? 'srp.pills.mileageMax' : 'srp.pills.mileage',
          {
            min: min.toLocaleString(),
            max: max.toLocaleString(),
          }
        )
      );
    },
    Transmission: (filters: CheckboxValue): FilterPill[] =>
      createCheckboxPills(t, 'Transmission', filters),
    TruckOptions: (filters: SrpTruckOptionsFilterValue): FilterPill[] =>
      createTruckOptionsPills(t, filters),
    SortBy: (): [] => [],
    Location: (): [] => [],
    Category: (value: SrpCategoryValue): FilterPill[] => createCategoryPills(t, value),
  };
}
